import React, { useMemo } from 'react';

import {
	AccountDisplayProps,
	EAccountDisplayState,
} from '@/components/AccountPanel/AccountPanel';
import { AsaphButton } from '@/components/shared/Button';
import {
	ISetting,
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { UserAvatar } from '@/components/UserAvatar';
import { ENV_OVERRIDE_STORAGE_KEY } from '@/config/constants';
import { Permissions } from '@/graphql';
import { sentenceCase } from '@/helpers/changeCase';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { useIntercom } from '@/providers/IntercomProvider/IntercomProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import { Capacitor } from '@capacitor/core';
import { useSnackbar } from 'notistack';
import { useCopyToClipboard, useLocalStorage } from 'react-use';

import styles from './AccountRoot.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

const AccountRoot: React.FC<AccountDisplayProps> = (props) => {
	const { logout, user, token } = useAuth();
	const { team: teamObject } = useTeam();
	const { services } = useServices();
	const isAdmin =
		teamObject.members
			.find((m) => m.user.id === user.id)
			?.roles.includes('teamAdmin') ?? false;

	const { statusLoading, setPaidModalOpen } = useSubscription();
	const {
		environmentString,
		api: { url },
		client,
		version,
	} = useEnvironment();
	const { darkModeSetting } = useDarkMode();
	const { hasPermission } = usePermissions();
	const [envOverride, setEnvOverride, removeEnvOverride] =
		useLocalStorage<string>(ENV_OVERRIDE_STORAGE_KEY);
	const { enqueueSnackbar } = useSnackbar();

	const { openIntercom } = useIntercom();
	const [, copyToClipboard] = useCopyToClipboard();
	const inviteLink = useMemo(
		() => `${client.url}/i/${teamObject.inviteLinkCode}`,
		[client.url, teamObject.inviteLinkCode],
	);

	const settings: SettingsListItems = [
		{
			type: 'setting',
			title: 'Edit profile',
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.EDIT_PROFILE),
		},
		{
			type: 'setting',
			title: 'Serving',
			onClick: () => props.pushDisplayState(EAccountDisplayState.SERVING),
		},
		// {
		// 	type: 'setting',
		// 	hidden: !hasPermission(Permissions.DevtoolsRead),
		// 	title: 'Dark mode',
		// 	trailingText: textTransformFirstLetterUpper(darkModeSetting),
		// 	onClick: () =>
		// 		props.pushDisplayState(EAccountDisplayState.DARK_MODE),
		// },
		{
			type: 'setting',
			title: 'Notifications',
			condition: isCapacitor,
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.PUSH_NOTIFICATIONS),
		},
		{
			type: 'title',
			title: 'Church',
		},
		{
			type: 'setting',
			title: 'Church profile',
			subtitle: teamObject.name,
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.CHURCH_PROFILE),
		},
		{
			type: 'setting',
			title: isAdmin ? 'Manage services' : 'Services',
			subtitle: services.map((s) => s.name).join(', '),
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.SERVICES),
		},
		{
			type: 'title',
			title: 'Team',
		},
		{
			type: 'setting',
			title: 'Team members',
			onClick: () => props.pushDisplayState(EAccountDisplayState.TEAM),
		},
		{
			type: 'setting',
			title: 'Copy invite link',
			onClick: () => {
				copyToClipboard(inviteLink);
				enqueueSnackbar('Link copied', { variant: 'success' });
			},
		},
		{
			type: 'title',
			title: 'Songs',
		},
		{
			type: 'setting',
			title: 'Chart storage',
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.CHART_STORAGE),
		},
		{
			type: 'title',
			title: `Account`,
		},
		{
			type: 'setting',
			title: 'Account settings',
			orderPosition: 'first',
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.ACCOUNT_SETTINGS),
		},
		{
			type: 'setting',
			title: 'Plan',
			orderPosition: 'middle',
			onClick: () =>
				props.pushDisplayState(EAccountDisplayState.USAGE_AND_LIMITS),
		},
		{
			id: 'purchase',
			type: 'setting',
			title: statusLoading ? 'Loading...' : 'Upgrade',
			disabled: statusLoading,
			condition: !isCapacitor,
			onClick: () => setPaidModalOpen(true),
		},
		{
			id: 'intercom',
			type: 'setting',
			title: 'Give feedback',
			orderPosition: 'middle',
			onClick: () => {
				props.setOpen({
					open: false,
				});
				handleHelpPress();
			},
		},
		{
			type: 'setting',
			title: 'Logout',
			onClick: () => {
				logout();
				props.setOpen({
					open: false,
				});
			},
		},
		{
			type: 'title',
			title: 'Dev tools',
			hidden: !hasPermission(Permissions.DevtoolsRead),
		},
		{
			type: 'setting',
			hidden: !hasPermission(Permissions.DevtoolsRead),
			title: 'GraphQL Playground',
			onClick: () => {
				window.open(
					`https://studio.apollographql.com/sandbox/explorer?endpoint=${encodeURIComponent(
						`${url}/graphql`,
					)}&headers=${encodeURIComponent(
						`{"Authorization": "Bearer ${token}"}`,
					)}`,
				);
			},
		},
	];

	// Mobile: hide "Upgrade" setting
	if (isCapacitor) {
		const upgradeItem: ISetting = settings.find(
			(item) => (item as ISetting).id === 'purchase',
		) as ISetting;
		settings.splice(settings.indexOf(upgradeItem), 1);
	}

	const avatar = (
		<UserAvatar size="x-large" userId={user.id} activeSize="medium" />
	);
	const userAvatar = avatar;
	// const userAvatar = user?.settingDisplayLastActive ? (
	// 	<Badge
	// 		anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
	// 		overlap="circle"
	// 		variant="dot"
	// 		classes={{ badge: styles['account-root__user-image-badge'] }}
	// 	>
	// 		{avatar}
	// 	</Badge>
	// ) : (
	// 	avatar
	// );

	return (
		<div className={styles['account-root']}>
			<div className={styles['account-root__user']}>
				<div className={styles['account-root__user-image']}>
					{userAvatar}
				</div>
				<div className={styles['account-root__user-name']}>
					<span>{user.displayName}</span>
				</div>
				<div className={styles['account-root__user-name-subtitle']}>
					{user.skills.map((s) => sentenceCase(s)).join(', ')}
				</div>
			</div>
			<div className="vsb-20" />
			<SettingsList
				className={styles['account-root__list']}
				items={settings}
				noChevron
			/>
			<div className="vsb-20" />
			<div className={styles['version']}>Version {version}</div>
			<div className="vsb-20" />
			<div className={styles['envs']}>
				{hasPermission(Permissions.DevtoolsRead) ? (
					<>
						<div className={styles['set']}>
							{['PRODUCTION', 'STAGING', 'LOCAL'].map((env) => (
								<AsaphButton
									key={`env-${env}`}
									onClick={() => {
										setEnvOverride(env);
										enqueueSnackbar(
											'Environment override set. You need to restart the app for this to take effect.',
										);
										logout();
									}}
									color={
										envOverride === env ? 'blue' : 'grey'
									}
									label={env}
								/>
							))}
						</div>
						<div className={styles['clear']}>
							<span>
								Current env <strong>{environmentString}</strong>
								.
							</span>
						</div>
						<div className={styles['clear']}>
							<span>
								Team id <strong>{teamObject.id}</strong>.
							</span>
						</div>
					</>
				) : null}
				{envOverride ? (
					<div className={styles['clear']}>
						<span>
							Currently overriding with{' '}
							<strong>{envOverride}</strong> environment.
						</span>
						&nbsp;
						<a
							onClick={() => {
								removeEnvOverride();
								enqueueSnackbar(
									'Environment override cleared. You need to restart the app for this to take effect.',
								);
								logout();
							}}
						>
							Click here
						</a>
						&nbsp; to clear.
					</div>
				) : null}
			</div>
		</div>
	);

	function handleHelpPress() {
		openIntercom();
	}
};

export default AccountRoot;
